import { useEffect, useRef } from 'react';
import { FormGroup } from '@core/utils/form';

/*
 * Section / column text colours vs colours typed into the text.
 *
 * A block's Text Color (advanced settings) and a column's Text Color (multi-column text blocks) are CSS defaults: text
 * that carries its own colour from the editor ignores them. Most of those inline colours are plain black / white /
 * grey left over from earlier edits or pasting, so changing the section colour seemed to do nothing. When a text colour
 * is changed, plain inline colours in that scope are cleared so the new colour shows. Accent colours (red, blue, ...)
 * are kept. Headings get `color: inherit`, because the theme colours every h1-h6 grey, which would otherwise win over
 * the section colour. Nothing changes until a colour is changed, so existing proposals keep their look.
 */

// (`inherit` is not plain: it's what makes a heading follow the section colour)
const PLAIN_KEYWORDS = ['black', 'white', 'gray', 'grey', 'silver', 'initial'];

// Red, green and blue this close together read as black / white / grey (e.g. the theme's text grey rgb(73, 80, 87),
// often pasted in from the app). Accents are far apart, e.g. navy rgb(11, 83, 148).
const GREY_TOLERANCE = 16;

const isGrey = (channels: number[]) => Math.max(...channels) - Math.min(...channels) <= GREY_TOLERANCE;

// Black, white or a grey, or a keyword that resolves to one: a plain text colour.
// Inline styles come back as rgb(); <font color> attributes keep the hex they were written with.
function isPlainColor(color: string): boolean {
  const value = color.trim().toLowerCase();
  if (PLAIN_KEYWORDS.includes(value)) return true;
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(value);
  if (rgb) return isGrey([rgb[1], rgb[2], rgb[3]].map(Number));
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(value);
  if (!hex) return false;
  const digits = hex[1].length === 3 ? hex[1].replace(/./g, d => d + d) : hex[1];
  return isGrey([0, 2, 4].map(i => parseInt(digits.slice(i, i + 2), 16)));
}

/**
 * Removes plain inline text colours from rich-text HTML and makes headings without a colour of their own follow the
 * section colour. Returns the same string when nothing changed.
 */
export function clearPlainTextColors(html: string): string {
  if (!html || !/color|<h[1-6]/i.test(html)) return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  let changed = false;
  template.content.querySelectorAll<HTMLElement>('[style]').forEach(element => {
    const color = element.style.color;
    if (!color || !isPlainColor(color)) return;
    element.style.removeProperty('color');
    if (!element.getAttribute('style')?.trim()) element.removeAttribute('style');
    changed = true;
  });
  template.content.querySelectorAll('font[color]').forEach(font => {
    if (!isPlainColor(font.getAttribute('color') || '')) return;
    font.removeAttribute('color');
    changed = true;
  });
  template.content.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6').forEach(heading => {
    if (heading.style.color) return;
    heading.style.color = 'inherit';
    changed = true;
  });
  return changed ? template.innerHTML : html;
}

// Any HTML string inside a block's data (any depth); other values are returned as they are
function clearDeep<T>(value: T): T {
  if (typeof value === 'string') return (/<[a-z]/i.test(value) ? clearPlainTextColors(value) : value) as unknown as T;
  if (Array.isArray(value)) {
    const mapped = value.map(clearDeep);
    return (mapped.some((item, index) => item !== value[index]) ? mapped : value) as unknown as T;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      result[key] = clearDeep(item);
      changed = changed || result[key] !== item;
    });
    return (changed ? result : value) as T;
  }
  return value;
}

// Text-block columns: paragraph → column 1, paragraph2 → column 2, ...
const COLUMN_OF_FIELD: Record<string, number> = { paragraph: 1, paragraph2: 2, paragraph3: 3, paragraph4: 4 };

/**
 * The block's Text Color changed: clear plain inline colours in all of the block's text, except columns that have their
 * own Text Color (theirs wins over the section's, so they're left alone). `content` is the block's own form (the
 * parent of its advanced settings).
 */
export function applySectionTextColor(content: FormGroup<any>) {
  const data = content.value as Record<string, unknown>;
  const changes: Record<string, unknown> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key === 'advance') return;
    const column = COLUMN_OF_FIELD[key];
    if (column && data[`column${column}Color`]) return;
    const next = clearDeep(value);
    if (next !== value) changes[key] = next;
  });
  if (Object.keys(changes).length) content.patchValue(changes);
}

/** Runs `onChange` when the colour is changed to a new value (not on the first render, not when it's cleared). */
export function useOnTextColorChange(color: string | null | undefined, onChange: (color: string) => void) {
  const previous = useRef(color);
  const callback = useRef(onChange);
  callback.current = onChange;
  useEffect(() => {
    const before = (previous.current || '').toLowerCase();
    previous.current = color;
    if (color && color.toLowerCase() !== before) callback.current(color);
  }, [color]);
}
